import {
  ChevronLeft,
  ChevronRight,
  Error,
  Info,
  Menu,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { OrganizationUnit } from '~/data/orgunits/types';
import { OrgUnitLabel } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/OrgUnitLabel';
import { WorkflowConfigUnitSelector } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigUnitSelector';
import { WorkflowConfigurationsTable } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigurationsTable';
import { getOrgUnitLabel } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';
import { useWorkflowConfigurationView } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/useWorkflowConfigurationView';
import { theme } from '~/theme';

import { useSxStyles } from './useStyles';
export const WorkflowsConfigurationView = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    isLoading,
    orgUnits,
    unitSelector,
    hasData,
    configuredUnitIds,
    engineStatus,
  } = useWorkflowConfigurationView();

  const getUnitLabel = (orgUnit: OrganizationUnit | undefined) =>
    !!orgUnit ? getOrgUnitLabel(orgUnit) : '';

  return (
    <Box sx={sxStyles.viewRoot}>
      <Box sx={sxStyles.unitContent}>
        <Box sx={sxStyles.header}>
          <Typography variant={'h5'}>
            {t('laWorkflowConfig')}
            {unitSelector.enabled && !!unitSelector.selectedOrgUnit && (
              <>
                {' - '}
                <OrgUnitLabel
                  orgUnit={unitSelector.selectedOrgUnit}
                  variant={'inline'}
                />
              </>
            )}
            {!engineStatus && (
              <Tooltip
                sx={sxStyles.demoChip}
                title={
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Error fontSize={'small'} />
                    <Box>
                      <Typography variant={'body2'}>
                        Workflows 2.0 engine is not enabled.
                      </Typography>
                      <Typography variant={'caption'}>
                        Old engine and workflows are used instead.
                      </Typography>
                    </Box>
                  </Box>
                }
              >
                <Chip label={'Demo'} color={'info'} icon={<Info />} />
              </Tooltip>
            )}
          </Typography>
          <Box>
            {unitSelector.enabled && (
              <Button
                onClick={unitSelector.toggle}
                variant={'outlined'}
                startIcon={<Menu />}
              >
                {`${t('laSelectUnit')}: ${getUnitLabel(
                  unitSelector.selectedOrgUnit,
                )}`}
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={sxStyles.content}>
          {isLoading && (
            <Box sx={sxStyles.loading}>
              <CircularProgress />
            </Box>
          )}
          {hasData && (
            <WorkflowConfigurationsTable
              orgUnit={unitSelector.selectedOrgUnit}
              orgUnits={orgUnits}
              sx={{ height: '100%' }}
              unitConfigsEnabled={unitSelector.enabled}
            />
          )}
        </Box>
      </Box>
      <Box sx={sxStyles.unitSelectorSlot}>
        {hasData && (
          <Collapse
            in={unitSelector.isVisible}
            orientation={'horizontal'}
            sx={sxStyles.unitSelectorDrawer}
          >
            <Box sx={sxStyles.unitSelector}>
              <Box sx={sxStyles.unitSelectorTop}>
                <IconButton onClick={unitSelector.hide}>
                  {theme.direction === 'ltr' ? (
                    <ChevronRight />
                  ) : (
                    <ChevronLeft />
                  )}
                </IconButton>
                <Box>{t('laSelectUnit')}</Box>
              </Box>
              <Box sx={sxStyles.unitSelectorContent}>
                <WorkflowConfigUnitSelector
                  onUnitSelect={unitSelector.selectUnitById}
                  selectedUnitId={unitSelector.selectedUnitId}
                  configuredUnits={configuredUnitIds}
                />
              </Box>
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};
